import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthForm } from './components/auth/AuthForm';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { DashboardHome } from './components/dashboard/DashboardHome';
import { TaskList } from './components/tasks/TaskList';
import { TaskForm } from './components/tasks/TaskForm';
import { TaskDetails } from './components/tasks/TaskDetails';
import ProjectForm from './components/projects/ProjectForm';
import { ProjectDetails } from './components/projects/ProjectDetails';
import { ProjectList } from './components/projects/ProjectList';
import { ClientsPage } from './components/clients/ClientsPage';
import { LeadsPage } from './components/leads/LeadsPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { LandingPage } from './pages/landing/LandingPage';
import { ContractForm } from './components/contracts/ContractForm';
import { AuthContext } from './lib/auth';
import { supabase } from './lib/supabase';
import type { User } from './types';
import { Session } from '@supabase/supabase-js';
import { MaintenancePage } from './pages/maintenance/MaintenancePage';
import { Messages } from './components/messages/Messages';
import { ClientPortal } from './pages/clientportal/clientPortal';
import { TeamPage } from './components/team/TeamPage';
import { DocumentList } from './components/documents/DocumentList';
import { DocumentEditor } from './components/documents/DocumentEditor';
import { JoinTeam } from './components/team/JoinTeam';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { UpdatePasswordForm } from './components/auth/UpdatePasswordForm';
import { initGA, pageview } from './lib/analytics';

// Create a new component to handle route changes
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
}

export default function App() {
  // const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  const MAINTENANCE_MODE = 'false';


  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
        });
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <AuthContext.Provider value={{ user, loading, session }}>
      <Router>
        <RouteTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
          <Route
            path="/login"
            element={user ? <Navigate to="/dashboard" replace /> : <AuthForm type="login" />}
          />
          <Route
            path="/signup"
            element={user ? <Navigate to="/dashboard" replace /> : <AuthForm type="signup" />}
          />

          {/* Client Portal route - accessible without auth */}
          <Route path="/client-portal" element={<ClientPortal />} />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={user ? <DashboardLayout /> : <Navigate to="/login" replace />}
          >
            <Route index element={<DashboardHome />} />

            {/* Project Routes */}
            <Route path="projects">
              <Route index element={<ProjectList />} />
              <Route path="new" element={<ProjectForm />} />
              <Route path="edit/:id" element={<ProjectForm />} />
              <Route path=":id" element={<ProjectDetails />} />
              <Route path=":projectId/contracts/new" element={<ContractForm />} />
              <Route path=":projectId/contracts/:id" element={<ProjectDetails />} />
            </Route>

            {/* Task Routes */}
            <Route path="tasks">
              <Route index element={<TaskList />} />
              <Route path="new" element={<TaskForm mode="create" />} />
              <Route path=":taskId" element={<TaskDetails id={''} projectId={''} onClose={function (): void {
                throw new Error('Function not implemented.');
              }} onUpdate={function (): Promise<void> {
                throw new Error('Function not implemented.');
              }} />} />
              <Route path="edit/:taskId" element={<TaskForm mode="edit" />} />
            </Route>

            {/* Leads Routes */}
            <Route path="leads" element={<LeadsPage />} />

            {/* Messages Route */}
            <Route path="messages" element={<Messages />} />

            {/* Other Routes */}
            <Route path="clients" element={<ClientsPage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* Team Routes */}
            <Route
              path="team"
              element={
                session?.user?.user_metadata?.role === 'Solo Freelancer'
                  ? <Navigate to="/dashboard" replace />
                  : <TeamPage />
              }
            />

            {/* Document Routes */}
            <Route path="documents">
              <Route index element={<DocumentList />} />
              <Route path="new" element={<DocumentEditor />} />
              <Route path=":id" element={<DocumentEditor />} />
            </Route>
          </Route>

          {/* Add this new test route that doesn't require authentication */}
          <Route path="/client-portal-test" element={<ClientPortal />} />

          {/* Join Team route */}
          <Route path="/join-team" element={<JoinTeam />} />

          {/* Reset Password route */}
          <Route path="/reset-password" element={<ResetPasswordForm />} />

          {/* Update Password route */}
          <Route path="/update-password" element={<UpdatePasswordForm />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}